// Buddy Owner Panel — 13 modules in one file (kept concise per module for context efficiency)
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ownerApi } from "./ownerApi";
import { LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";

const COLORS = ["#4FD18E", "#C9A96E", "#79A6FF", "#FF7A8A", "#9C7BFF", "#FFB347"];

// ── Shared Card / Header ───────────────────────────────────
export const Card = ({ children, className = "", ...rest }) => (
  <div className={`rounded-xl p-4 ${className}`} style={{ background: "#11181F", border: "1px solid #1F2A33" }} {...rest}>{children}</div>
);
export const KpiCard = ({ label, value, sub, tone, testid }) => (
  <Card data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider" style={{ color: "#7A7A72" }}>{label}</p>
    <p className="font-display text-2xl font-bold mt-1" style={{ color: tone === "good" ? "#4FD18E" : tone === "warn" ? "#C9A96E" : tone === "bad" ? "#FF7A8A" : "#E8E4DC" }}>{value}</p>
    {sub && <p className="text-[10px] mt-1" style={{ color: "#7A7A72" }}>{sub}</p>}
  </Card>
);
export const PageHeader = ({ title, subtitle, action }) => (
  <header className="px-6 py-5 flex items-center justify-between" style={{ borderBottom: "1px solid #1F2A33" }}>
    <div>
      <h1 className="font-display text-xl font-bold">{title}</h1>
      {subtitle && <p className="text-xs mt-0.5" style={{ color: "#7A7A72" }}>{subtitle}</p>}
    </div>
    {action}
  </header>
);

// ═════════════════════════════════════════════════════════════
// 1. DASHBOARD
// ═════════════════════════════════════════════════════════════
export function OwnerDashboard() {
  const [m, setM] = useState(null);
  const [charts, setCharts] = useState(null);
  const [live, setLive] = useState(null);
  useEffect(() => {
    ownerApi.get("/dashboard/metrics").then((r) => setM(r.data)).catch(() => {});
    ownerApi.get("/dashboard/charts").then((r) => setCharts(r.data)).catch(() => {});
    ownerApi.get("/dashboard/live").then((r) => setLive(r.data)).catch(() => {});
  }, []);
  if (!m) return <div className="p-6 text-xs" style={{ color: "#7A7A72" }}>Loading dashboard…</div>;
  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Real-time platform overview" />
      <div className="p-6 space-y-5">
        {/* KPI Row */}
        <div className="grid grid-cols-4 gap-3">
          <KpiCard label="Total users" value={m.total_users.toLocaleString()} sub={`${m.active_30d.toLocaleString()} active (30d)`} testid="kpi-users" />
          <KpiCard label="Paid subscribers" value={m.paid_subscribers.toLocaleString()} sub={`${m.conversion_rate}% conversion`} tone="good" testid="kpi-paid" />
          <KpiCard label="MRR" value={`$${m.mrr.toLocaleString()}`} sub={`${m.revenue_growth_pct >= 0 ? "+" : ""}${m.revenue_growth_pct}% vs last mo`} tone="good" testid="kpi-mrr" />
          <KpiCard label="AI cost today" value={`$${m.ai_cost_today}`} sub={`${(m.ai_tokens_today/1000).toFixed(0)}k tokens`} tone="warn" testid="kpi-ai-cost" />
          <KpiCard label="Total revenue" value={`$${m.total_revenue.toLocaleString()}`} sub="Lifetime (paid only)" />
          <KpiCard label="ARR" value={`$${m.arr.toLocaleString()}`} sub="Annual run-rate" tone="good" />
          <KpiCard label="Pro users" value={m.active_pro_users ?? 0} sub="Active Pro plan" />
          <KpiCard label="Team users" value={m.active_team_users ?? 0} sub="Active Teams plan" />
          <KpiCard label="Free users" value={m.free_users.toLocaleString()} sub={`${(100 - m.conversion_rate).toFixed(1)}% of base`} />
          <KpiCard label="Failed payments" value={m.failed_payments} tone={m.failed_payments > 0 ? "warn" : ""} sub="This month" />
          <KpiCard label="Refunds (mo)" value={`$${(m.refund_amount_month || 0).toLocaleString()}`} sub={`${m.refund_count_month} refunds`} />
          <KpiCard label="System" value={m.system_health.toUpperCase()} tone={m.system_health === "green" ? "good" : "bad"} testid="kpi-health" />
        </div>
        {/* Live monitor */}
        {live && (
          <Card>
            <p className="text-[10px] uppercase tracking-wider mb-3" style={{ color: "#7A7A72" }}>Live monitor</p>
            <div className="grid grid-cols-5 gap-3 text-center">
              <div><p className="text-xl font-bold" style={{ color: "#4FD18E" }}>{live.active_sessions}</p><p className="text-[10px]" style={{ color: "#7A7A72" }}>Active now</p></div>
              <div><p className="text-xl font-bold">{live.ai_req_per_sec}</p><p className="text-[10px]" style={{ color: "#7A7A72" }}>AI req/sec</p></div>
              <div><p className="text-xl font-bold" style={{ color: live.error_rate_5min > 2 ? "#FF7A8A" : "#4FD18E" }}>{live.error_rate_5min}%</p><p className="text-[10px]" style={{ color: "#7A7A72" }}>Error rate</p></div>
              <div><p className="text-xl font-bold">{live.latency_p95_ms}ms</p><p className="text-[10px]" style={{ color: "#7A7A72" }}>p95 latency</p></div>
              <div><p className="text-xl font-bold" style={{ color: "#4FD18E" }}>{live.uptime_pct}%</p><p className="text-[10px]" style={{ color: "#7A7A72" }}>Uptime</p></div>
            </div>
          </Card>
        )}
        {/* Charts */}
        {charts && (
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <p className="text-[10px] uppercase tracking-wider mb-3" style={{ color: "#7A7A72" }}>Revenue (12mo)</p>
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={charts.monthly}><XAxis dataKey="month" stroke="#4A5560" fontSize={10} /><YAxis stroke="#4A5560" fontSize={10} /><Tooltip contentStyle={{ background: "#070B0F", border: "1px solid #1F2A33", fontSize: 11 }} /><Area dataKey="revenue" stroke="#4FD18E" fill="#4FD18E" fillOpacity={0.18} /></AreaChart>
              </ResponsiveContainer>
            </Card>
            <Card>
              <p className="text-[10px] uppercase tracking-wider mb-3" style={{ color: "#7A7A72" }}>Signups (free vs paid)</p>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={charts.monthly}><XAxis dataKey="month" stroke="#4A5560" fontSize={10} /><YAxis stroke="#4A5560" fontSize={10} /><Tooltip contentStyle={{ background: "#070B0F", border: "1px solid #1F2A33", fontSize: 11 }} /><Bar dataKey="free" stackId="a" fill="#79A6FF" /><Bar dataKey="paid" stackId="a" fill="#4FD18E" /></BarChart>
              </ResponsiveContainer>
            </Card>
            <Card>
              <p className="text-[10px] uppercase tracking-wider mb-3" style={{ color: "#7A7A72" }}>AI cost trend (30d)</p>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={charts.ai_trend.slice(-30)}><XAxis dataKey="date" stroke="#4A5560" fontSize={9} interval={4} /><YAxis stroke="#4A5560" fontSize={10} /><Tooltip contentStyle={{ background: "#070B0F", border: "1px solid #1F2A33", fontSize: 11 }} /><Line dataKey="cost" stroke="#C9A96E" dot={false} strokeWidth={2} /></LineChart>
              </ResponsiveContainer>
            </Card>
            <Card>
              <p className="text-[10px] uppercase tracking-wider mb-3" style={{ color: "#7A7A72" }}>Plan distribution</p>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart><Pie data={charts.plan_distribution || charts.platform_breakdown || []} dataKey="value" outerRadius={60} label={{ fontSize: 10 }}>{(charts.plan_distribution || charts.platform_breakdown || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Legend wrapperStyle={{ fontSize: 10 }} /></PieChart>
              </ResponsiveContainer>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// 2. USERS
// ═════════════════════════════════════════════════════════════
export function OwnerUsers() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [plan, setPlan] = useState("");
  useEffect(() => {
    ownerApi.get("/users", { params: { search, plan, page: 1, page_size: 50 } }).then((r) => { setUsers(r.data.users); setTotal(r.data.total); });
  }, [search, plan]);
  return (
    <div>
      <PageHeader title="Users" subtitle={`${total.toLocaleString()} total`} />
      <div className="p-6 space-y-3">
        <div className="flex gap-2">
          <input placeholder="Search by email…" value={search} onChange={(e) => setSearch(e.target.value)} className="flex-1 px-3 py-2 rounded-lg text-xs outline-none" style={{ background: "#11181F", border: "1px solid #1F2A33", color: "#E8E4DC" }} data-testid="users-search" />
          <select value={plan} onChange={(e) => setPlan(e.target.value)} className="px-3 py-2 rounded-lg text-xs" style={{ background: "#11181F", border: "1px solid #1F2A33", color: "#E8E4DC" }} data-testid="users-plan-filter"><option value="">All plans</option><option value="free">Free</option><option value="pro">Pro</option><option value="teams">Teams</option></select>
        </div>
        <Card className="!p-0 overflow-hidden">
          <table className="w-full text-xs" data-testid="users-table">
            <thead><tr style={{ background: "#0A0F14", color: "#7A7A72" }}>
              {["Email", "Plan", "Status", "Joined", "Last active", ""].map((h) => <th key={h} className="text-left px-4 py-2.5 font-semibold uppercase tracking-wider text-[10px]">{h}</th>)}
            </tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} style={{ borderTop: "1px solid #1F2A33" }} data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-2.5">{u.email}</td>
                  <td className="px-4 py-2.5"><span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: u.plan === "free" ? "#2A2A2A" : u.plan === "pro" ? "#0F2A1F" : "#2A1F0F", color: u.plan === "free" ? "#9AA5B0" : u.plan === "pro" ? "#4FD18E" : "#C9A96E" }}>{u.plan?.toUpperCase()}</span></td>
                  <td className="px-4 py-2.5">{u.is_suspended ? <span style={{ color: "#FF7A8A" }}>Suspended</span> : <span style={{ color: "#4FD18E" }}>Active</span>}</td>
                  <td className="px-4 py-2.5" style={{ color: "#7A7A72" }}>{(u.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-2.5" style={{ color: "#7A7A72" }}>{(u.last_active_at || "—").slice(0, 10)}</td>
                  <td className="px-4 py-2.5"><Link to={`/owner/users/${u.id}`} className="text-[11px]" style={{ color: "#4FD18E" }}>View →</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}

// 2b. USER DETAIL
export function OwnerUserDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const reload = () => ownerApi.get(`/users/${id}`).then((r) => setData(r.data));
  useEffect(() => { reload(); }, [id]);
  if (!data) return <div className="p-6 text-xs" style={{ color: "#7A7A72" }}>Loading…</div>;
  const { user, stats } = data;
  const act = async (path, body) => { try { await ownerApi.post(`/users/${id}/${path}`, body || {}); reload(); } catch (e) { alert(e.response?.data?.detail || "Action failed"); } };
  return (
    <div>
      <PageHeader title={user.email} subtitle={`User • ${user.plan?.toUpperCase()} • ${user.is_suspended ? "SUSPENDED" : "ACTIVE"}`} action={
        <div className="flex gap-2">
          {user.is_suspended ? <button onClick={() => act("unsuspend")} className="text-xs px-3 py-1.5 rounded-lg" style={{ background: "#0F2A1F", color: "#4FD18E" }} data-testid="user-unsuspend">Unsuspend</button> : <button onClick={() => { const reason = prompt("Reason?"); if (reason) act("suspend", { reason }); }} className="text-xs px-3 py-1.5 rounded-lg" style={{ background: "#3a1a1f", color: "#FF7A8A" }} data-testid="user-suspend">Suspend</button>}
          <select onChange={(e) => act("upgrade-plan", { plan: e.target.value })} className="text-xs px-3 py-1.5 rounded-lg" style={{ background: "#11181F", border: "1px solid #1F2A33", color: "#E8E4DC" }} data-testid="user-plan-change"><option>Change plan…</option><option value="free">Free</option><option value="pro">Pro</option><option value="teams">Teams</option></select>
        </div>
      } />
      <div className="p-6 grid grid-cols-4 gap-3">
        <KpiCard label="Rewrites" value={stats.rewrites_total.toLocaleString()} />
        <KpiCard label="Tokens" value={stats.tokens_lifetime.toLocaleString()} />
        <KpiCard label="AI cost" value={`$${stats.cost_lifetime}`} />
        <KpiCard label="Churn risk" value={stats.churn_risk.toUpperCase()} tone={stats.churn_risk === "low" ? "good" : "warn"} />
        <KpiCard label="Conversion" value={`${stats.conversion_likelihood}%`} />
        <KpiCard label="Engagement" value={`${stats.engagement_score}/100`} />
        <KpiCard label="Retention" value={`${stats.retention_score}/100`} />
        <KpiCard label="Verified" value={user.email_verified ? "Yes" : "No"} tone={user.email_verified ? "good" : "warn"} />
      </div>
      <div className="px-6 pb-6"><Card><pre className="text-[11px] overflow-x-auto" style={{ color: "#9AA5B0" }}>{JSON.stringify(user, null, 2)}</pre></Card></div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// 3. TEAMS
// ═════════════════════════════════════════════════════════════
export function OwnerTeams() {
  const [teams, setTeams] = useState([]);
  useEffect(() => { ownerApi.get("/teams").then((r) => setTeams(r.data.teams)); }, []);
  return (
    <div>
      <PageHeader title="Teams" subtitle="Workspace accounts" />
      <div className="p-6"><Card className="!p-0"><table className="w-full text-xs"><thead><tr style={{ background: "#0A0F14", color: "#7A7A72" }}>{["Name", "Members", "Plan", "AI usage (mo)", "MRR"].map((h) => <th key={h} className="text-left px-4 py-2.5 font-semibold uppercase tracking-wider text-[10px]">{h}</th>)}</tr></thead><tbody>{teams.map((t) => <tr key={t.id} style={{ borderTop: "1px solid #1F2A33" }} data-testid={`team-row-${t.id}`}><td className="px-4 py-2.5">{t.name}</td><td className="px-4 py-2.5">{t.members}</td><td className="px-4 py-2.5">{t.plan?.toUpperCase()}</td><td className="px-4 py-2.5">{t.ai_usage_month.toLocaleString()}</td><td className="px-4 py-2.5">${t.mrr}</td></tr>)}</tbody></table></Card></div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// 4. AI COSTS
// ═════════════════════════════════════════════════════════════
export function OwnerAICosts() {
  const [overview, setOverview] = useState(null);
  const [byFeat, setByFeat] = useState([]);
  const [heavy, setHeavy] = useState([]);
  useEffect(() => {
    ownerApi.get("/ai-costs/overview").then((r) => setOverview(r.data));
    ownerApi.get("/ai-costs/by-feature").then((r) => setByFeat(r.data.by_feature));
    ownerApi.get("/ai-costs/heavy-users").then((r) => setHeavy(r.data.users));
  }, []);
  if (!overview) return <div className="p-6 text-xs" style={{ color: "#7A7A72" }}>Loading…</div>;
  return (
    <div>
      <PageHeader title="AI Costs" subtitle="LLM spend, tokens, and abuse detection" />
      <div className="p-6 space-y-5">
        <div className="grid grid-cols-3 gap-3">
          <KpiCard label="Today" value={`$${overview.today_spend}`} testid="aic-today" />
          <KpiCard label="This month" value={`$${overview.month_spend}`} sub={`projected $${overview.projected_month}`} tone="warn" />
          <KpiCard label="Cost/revenue ratio" value={overview.cost_revenue_ratio} tone={overview.cost_revenue_ratio > 0.3 ? "bad" : "good"} />
          <KpiCard label="Avg cost / user / day" value={`$${overview.avg_cost_per_user_day}`} />
          <KpiCard label="Avg cost / request" value={`$${overview.avg_cost_per_request}`} />
          <KpiCard label="Projected" value={`$${overview.projected_month}`} sub="end-of-month" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Card>
            <p className="text-[10px] uppercase tracking-wider mb-3" style={{ color: "#7A7A72" }}>Cost by feature</p>
            {byFeat.length === 0 ? <p className="text-xs" style={{ color: "#7A7A72" }}>No tool usage data yet.</p> : <table className="w-full text-xs"><thead><tr style={{ color: "#7A7A72" }}><th className="text-left py-1.5 text-[10px] uppercase tracking-wider">Feature</th><th className="text-right py-1.5 text-[10px] uppercase tracking-wider">Reqs</th><th className="text-right py-1.5 text-[10px] uppercase tracking-wider">Tokens</th><th className="text-right py-1.5 text-[10px] uppercase tracking-wider">Cost</th></tr></thead><tbody>{byFeat.map((f, i) => <tr key={i} style={{ borderTop: "1px solid #1F2A33" }}><td className="py-1.5">{f.feature}</td><td className="text-right">{f.requests}</td><td className="text-right">{f.tokens.toLocaleString()}</td><td className="text-right" style={{ color: "#C9A96E" }}>${f.cost}</td></tr>)}</tbody></table>}
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-wider mb-3" style={{ color: "#7A7A72" }}>Heavy users</p>
            <table className="w-full text-xs"><thead><tr style={{ color: "#7A7A72" }}><th className="text-left py-1.5 text-[10px] uppercase tracking-wider">Email</th><th className="text-left py-1.5 text-[10px] uppercase tracking-wider">Plan</th><th className="text-right py-1.5 text-[10px] uppercase tracking-wider">Rewrites</th><th className="text-right py-1.5 text-[10px] uppercase tracking-wider">Cost</th></tr></thead><tbody>{heavy.slice(0, 10).map((u, i) => <tr key={i} style={{ borderTop: "1px solid #1F2A33" }}><td className="py-1.5">{u.email}</td><td>{u.plan}</td><td className="text-right">{u.rewrites}</td><td className="text-right" style={{ color: "#C9A96E" }}>${u.estimated_cost}</td></tr>)}</tbody></table>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// 5. BILLING
// ═════════════════════════════════════════════════════════════
export function OwnerBilling() {
  const [ov, setOv] = useState(null);
  const [subs, setSubs] = useState([]);
  const [payments, setPayments] = useState([]);
  useEffect(() => {
    ownerApi.get("/billing/overview").then((r) => setOv(r.data));
    ownerApi.get("/billing/subscriptions").then((r) => setSubs(r.data.subscriptions));
    ownerApi.get("/payments").then((r) => setPayments(r.data.payments || [])).catch(() => setPayments([]));
  }, []);
  if (!ov) return <div className="p-6 text-xs" style={{ color: "#7A7A72" }}>Loading…</div>;
  return (
    <div>
      <PageHeader title="Billing & Revenue" subtitle="From real Stripe + Razorpay payments only — no estimates" />
      <div className="p-6 space-y-5">
        <div className="grid grid-cols-6 gap-3">
          <KpiCard label="Total revenue" value={`$${(ov.total_revenue || 0).toLocaleString()}`} tone="good" sub="Lifetime, paid only" />
          <KpiCard label="MRR" value={`$${ov.mrr.toLocaleString()}`} tone="good" testid="bill-mrr" sub="Current month" />
          <KpiCard label="ARR" value={`$${ov.arr.toLocaleString()}`} tone="good" sub="MRR × 12" />
          <KpiCard label="ARPU" value={`$${ov.arpu}`} sub="Avg per paid user" />
          <KpiCard label="LTV" value={`$${ov.ltv}`} sub="ARPU × 20" />
          <KpiCard label="Failed / refunded" value={`${ov.failed_payments} / $${(ov.refunded_amount || 0).toLocaleString()}`} tone={ov.failed_payments > 5 ? "bad" : ""} />
        </div>
        <Card>
          <p className="text-[10px] uppercase tracking-wider mb-3" style={{ color: "#7A7A72" }}>Active subscriptions ({subs.length})</p>
          {subs.length === 0 ? <p className="text-xs" style={{ color: "#7A7A72" }}>No active paid subscribers yet. Revenue will appear here as soon as users complete a Stripe or Razorpay checkout.</p> : <table className="w-full text-xs"><thead><tr style={{ color: "#7A7A72" }}><th className="text-left py-1.5 text-[10px] uppercase tracking-wider">Email</th><th className="text-left py-1.5 text-[10px] uppercase tracking-wider">Plan</th><th className="text-left py-1.5 text-[10px] uppercase tracking-wider">Since</th></tr></thead><tbody>{subs.slice(0, 20).map((s) => <tr key={s.id} style={{ borderTop: "1px solid #1F2A33" }}><td className="py-1.5">{s.email}</td><td>{s.plan?.toUpperCase()}</td><td style={{ color: "#7A7A72" }}>{(s.created_at || "").slice(0, 10)}</td></tr>)}</tbody></table>}
        </Card>
        <Card>
          <p className="text-[10px] uppercase tracking-wider mb-3" style={{ color: "#7A7A72" }}>Payment history ({payments.length})</p>
          {payments.length === 0 ? <p className="text-xs" style={{ color: "#7A7A72" }}>No payments recorded yet. Stripe + Razorpay webhooks will populate this list automatically.</p> : <table className="w-full text-xs"><thead><tr style={{ color: "#7A7A72" }}><th className="text-left py-1.5 text-[10px] uppercase tracking-wider">User</th><th className="text-left py-1.5 text-[10px] uppercase tracking-wider">Plan</th><th className="text-left py-1.5 text-[10px] uppercase tracking-wider">Amount</th><th className="text-left py-1.5 text-[10px] uppercase tracking-wider">Provider</th><th className="text-left py-1.5 text-[10px] uppercase tracking-wider">Status</th><th className="text-left py-1.5 text-[10px] uppercase tracking-wider">Date</th></tr></thead><tbody>{payments.slice(0, 50).map((p, idx) => <tr key={idx} style={{ borderTop: "1px solid #1F2A33" }}><td className="py-1.5">{p.user_email || p.user_id?.slice(0, 8)}</td><td>{(p.plan || "").toUpperCase()}</td><td>${p.final_amount_usd?.toFixed(2)}</td><td style={{ textTransform: "capitalize" }}>{p.provider}</td><td><span style={{ color: p.payment_status === "success" ? "#4FD18E" : p.payment_status === "refunded" ? "#C9A96E" : "#FF7A8A" }}>{p.payment_status}</span></td><td style={{ color: "#7A7A72" }}>{(p.paid_at || p.created_at || "").slice(0, 10)}</td></tr>)}</tbody></table>}
        </Card>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// 6. ANALYTICS
// ═════════════════════════════════════════════════════════════
export function OwnerAnalytics() {
  const [funnel, setFunnel] = useState([]);
  const [retention, setRetention] = useState([]);
  useEffect(() => {
    ownerApi.get("/analytics/funnel").then((r) => setFunnel(r.data.funnel));
    ownerApi.get("/analytics/retention").then((r) => setRetention(r.data.retention));
  }, []);
  return (
    <div>
      <PageHeader title="Analytics" subtitle="Funnels, retention, cohorts" />
      <div className="p-6 grid grid-cols-2 gap-4">
        <Card>
          <p className="text-[10px] uppercase tracking-wider mb-3" style={{ color: "#7A7A72" }}>Conversion funnel</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={funnel} layout="vertical"><XAxis type="number" stroke="#4A5560" fontSize={10} /><YAxis dataKey="stage" type="category" stroke="#4A5560" fontSize={10} width={80} /><Tooltip contentStyle={{ background: "#070B0F", border: "1px solid #1F2A33", fontSize: 11 }} /><Bar dataKey="count" fill="#4FD18E" /></BarChart>
          </ResponsiveContainer>
        </Card>
        <Card>
          <p className="text-[10px] uppercase tracking-wider mb-3" style={{ color: "#7A7A72" }}>Retention curve (D1–D90)</p>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={retention}><XAxis dataKey="day" stroke="#4A5560" fontSize={10} /><YAxis stroke="#4A5560" fontSize={10} /><Tooltip contentStyle={{ background: "#070B0F", border: "1px solid #1F2A33", fontSize: 11 }} /><Line dataKey="pct" stroke="#C9A96E" strokeWidth={2} /></LineChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// 7. ABUSE
// ═════════════════════════════════════════════════════════════
export function OwnerAbuse() {
  const [data, setData] = useState({ reports: [], total_flagged: 0 });
  const reload = () => ownerApi.get("/abuse").then((r) => setData(r.data));
  useEffect(() => { reload(); }, []);
  return (
    <div>
      <PageHeader title="Abuse Detection" subtitle={`${data.total_flagged} flagged, ${data.reports.length} total reports`} />
      <div className="p-6">
        <Card className="!p-0">
          <table className="w-full text-xs"><thead><tr style={{ background: "#0A0F14", color: "#7A7A72" }}>{["User", "Type", "Severity", "Status", "Date", ""].map((h) => <th key={h} className="text-left px-4 py-2.5 font-semibold uppercase tracking-wider text-[10px]">{h}</th>)}</tr></thead><tbody>{data.reports.length === 0 ? <tr><td className="px-4 py-6 text-center" style={{ color: "#7A7A72" }} colSpan={6}>No reports yet</td></tr> : data.reports.map((r) => <tr key={r.id} style={{ borderTop: "1px solid #1F2A33" }}><td className="px-4 py-2.5">{r.user_email || r.user_id}</td><td>{r.type}</td><td><span style={{ color: r.severity === "HIGH" ? "#FF7A8A" : r.severity === "MEDIUM" ? "#C9A96E" : "#9AA5B0" }}>{r.severity}</span></td><td>{r.status}</td><td style={{ color: "#7A7A72" }}>{(r.created_at || "").slice(0, 10)}</td><td><button onClick={async () => { await ownerApi.post(`/abuse/${r.id}/action`, { action: "dismiss" }); reload(); }} className="text-[11px] underline" style={{ color: "#4FD18E" }}>Dismiss</button></td></tr>)}</tbody></table>
        </Card>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// 8. SUPPORT
// ═════════════════════════════════════════════════════════════
export function OwnerSupport() {
  const [tab, setTab] = useState("tickets"); // "tickets" | "feedback"
  const [tickets, setTickets] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [status, setStatus] = useState("");
  useEffect(() => { ownerApi.get("/support/tickets", { params: { status } }).then((r) => setTickets(r.data.tickets)); }, [status]);
  useEffect(() => { if (tab === "feedback") ownerApi.get("/support/feedback").then((r) => setFeedback(r.data.feedback || [])); }, [tab]);
  const tabBtn = (id, label, count) => (
    <button
      onClick={() => setTab(id)}
      className="text-xs px-4 py-1.5 rounded-lg font-semibold transition-all"
      style={{
        background: tab === id ? "#0F2A1F" : "transparent",
        color: tab === id ? "#4FD18E" : "#7A7A72",
        border: `1px solid ${tab === id ? "#1F5A3A" : "#1F2A33"}`,
      }}
      data-testid={`support-tab-${id}`}
    >
      {label} <span style={{ opacity: 0.7 }}>· {count}</span>
    </button>
  );
  return (
    <div>
      <PageHeader
        title="Support"
        subtitle={tab === "tickets" ? `${tickets.length} tickets` : `${feedback.length} feedback`}
        action={
          <div className="flex items-center gap-2">
            {tabBtn("tickets", "Tickets", tickets.length)}
            {tabBtn("feedback", "Feedback", feedback.length)}
            {tab === "tickets" && (
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="text-xs px-3 py-1.5 rounded-lg" style={{ background: "#11181F", border: "1px solid #1F2A33", color: "#E8E4DC" }} data-testid="support-status-filter"><option value="">All</option><option>OPEN</option><option>IN_PROGRESS</option><option>RESOLVED</option></select>
            )}
          </div>
        }
      />
      <div className="p-6">
        {tab === "tickets" ? (
          <Card className="!p-0"><table className="w-full text-xs"><thead><tr style={{ background: "#0A0F14", color: "#7A7A72" }}>{["Subject", "User", "Priority", "Status", "Updated"].map((h) => <th key={h} className="text-left px-4 py-2.5 font-semibold uppercase tracking-wider text-[10px]">{h}</th>)}</tr></thead><tbody>{tickets.length === 0 ? <tr><td className="px-4 py-6 text-center" style={{ color: "#7A7A72" }} colSpan={5}>No tickets yet</td></tr> : tickets.map((t) => <tr key={t.id} style={{ borderTop: "1px solid #1F2A33" }}><td className="px-4 py-2.5">{t.subject}</td><td>{t.user_email}</td><td><span style={{ color: t.priority === "URGENT" ? "#FF7A8A" : t.priority === "HIGH" ? "#C9A96E" : "#9AA5B0" }}>{t.priority}</span></td><td>{t.status}</td><td style={{ color: "#7A7A72" }}>{(t.updated_at || t.created_at || "").slice(0, 10)}</td></tr>)}</tbody></table></Card>
        ) : (
          <div className="space-y-2" data-testid="feedback-list">
            {feedback.length === 0 ? (
              <Card><p className="text-xs" style={{ color: "#7A7A72" }}>No user feedback yet. Once users submit via the footer Feedback link, entries will appear here.</p></Card>
            ) : feedback.map((f) => (
              <Card key={f.id} data-testid={`feedback-item-${f.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p className="text-xs leading-relaxed" style={{ color: "#E8E4DC" }}>{f.message}</p>
                    <div className="mt-2 flex items-center gap-3 text-[10px]" style={{ color: "#7A7A72" }}>
                      <span>{f.email || "anonymous"}</span>
                      <span>·</span>
                      <span>{f.word_count} words</span>
                      <span>·</span>
                      <span>{(f.created_at || "").slice(0, 19).replace("T", " ")}</span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// 9. ALERTS
// ═════════════════════════════════════════════════════════════
export function OwnerAlerts() {
  const [data, setData] = useState({ alerts: [], unread_count: 0 });
  const reload = () => ownerApi.get("/alerts").then((r) => setData(r.data));
  useEffect(() => { reload(); }, []);
  return (
    <div>
      <PageHeader title="Alerts" subtitle={`${data.unread_count} unread`} />
      <div className="p-6 space-y-2">
        {data.alerts.length === 0 ? <Card><p className="text-xs" style={{ color: "#7A7A72" }}>No alerts right now.</p></Card> : data.alerts.map((a) => (
          <Card key={a.id} className="flex items-center justify-between" data-testid={`alert-${a.id}`}>
            <div>
              <p className="text-xs font-semibold" style={{ color: a.severity === "CRITICAL" ? "#FF7A8A" : a.severity === "WARNING" ? "#C9A96E" : "#79A6FF" }}>{a.title}</p>
              <p className="text-[11px] mt-1" style={{ color: "#9AA5B0" }}>{a.message}</p>
              <p className="text-[10px] mt-1" style={{ color: "#4A5560" }}>{(a.created_at || "").slice(0, 19).replace("T", " ")} · {a.type}</p>
            </div>
            {!a.is_read && <button onClick={async () => { await ownerApi.post(`/alerts/${a.id}/resolve`); reload(); }} className="text-[11px] px-3 py-1.5 rounded-lg" style={{ background: "#0F2A1F", color: "#4FD18E" }}>Resolve</button>}
          </Card>
        ))}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// 10. FEATURE FLAGS
// ═════════════════════════════════════════════════════════════
export function OwnerFeatures() {
  const [flags, setFlags] = useState([]);
  const reload = () => ownerApi.get("/features").then((r) => setFlags(r.data.flags));
  useEffect(() => { reload(); }, []);
  const toggle = async (key, on) => { await ownerApi.post(`/features/${key}/toggle`, { is_enabled: on }); reload(); };
  const setPct = async (key, pct) => { await ownerApi.post(`/features/${key}/toggle`, { rollout_pct: pct }); reload(); };
  return (
    <div>
      <PageHeader title="Feature Flags" subtitle="Kill switches & rollout %" />
      <div className="p-6 space-y-2">
        {flags.length === 0 ? <Card><p className="text-xs" style={{ color: "#7A7A72" }}>No flags configured yet. Create them via API or seed script.</p></Card> : flags.map((f) => (
          <Card key={f.key} className="flex items-center justify-between" data-testid={`flag-${f.key}`}>
            <div>
              <p className="text-sm font-semibold">{f.name || f.key}</p>
              <p className="text-[11px]" style={{ color: "#7A7A72" }}>{f.key}</p>
            </div>
            <div className="flex items-center gap-3">
              <input type="number" min={0} max={100} defaultValue={f.rollout_pct || 0} onBlur={(e) => setPct(f.key, parseInt(e.target.value) || 0)} className="w-16 text-xs px-2 py-1 rounded-md text-center" style={{ background: "#0A0F14", border: "1px solid #1F2A33", color: "#E8E4DC" }} /> <span className="text-[10px]" style={{ color: "#7A7A72" }}>%</span>
              <label className="relative inline-block w-10 h-5">
                <input type="checkbox" defaultChecked={f.is_enabled} onChange={(e) => toggle(f.key, e.target.checked)} className="opacity-0 absolute" data-testid={`flag-toggle-${f.key}`} />
                <span className="block w-10 h-5 rounded-full" style={{ background: f.is_enabled ? "#4FD18E" : "#1F2A33" }}></span>
                <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform" style={{ transform: f.is_enabled ? "translateX(22px)" : "translateX(2px)" }}></span>
              </label>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// 11. AUDIT LOG
// ═════════════════════════════════════════════════════════════
export function OwnerAudit() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [action, setAction] = useState("");
  useEffect(() => { ownerApi.get("/audit", { params: { action, page: 1, page_size: 100 } }).then((r) => { setLogs(r.data.logs); setTotal(r.data.total); }); }, [action]);
  return (
    <div>
      <PageHeader title="Audit Log" subtitle={`${total.toLocaleString()} immutable actions`} action={
        <input placeholder="Filter action…" value={action} onChange={(e) => setAction(e.target.value)} className="text-xs px-3 py-1.5 rounded-lg" style={{ background: "#11181F", border: "1px solid #1F2A33", color: "#E8E4DC" }} data-testid="audit-filter" />
      } />
      <div className="p-6"><Card className="!p-0"><table className="w-full text-xs"><thead><tr style={{ background: "#0A0F14", color: "#7A7A72" }}>{["When", "Admin", "Action", "Entity", "ID"].map((h) => <th key={h} className="text-left px-4 py-2.5 font-semibold uppercase tracking-wider text-[10px]">{h}</th>)}</tr></thead><tbody>{logs.map((l) => <tr key={l.id} style={{ borderTop: "1px solid #1F2A33" }} data-testid={`audit-row-${l.id}`}><td className="px-4 py-2.5" style={{ color: "#7A7A72" }}>{(l.created_at || "").slice(0, 19).replace("T", " ")}</td><td className="px-4">{l.admin_email}</td><td className="px-4"><span className="px-2 py-0.5 rounded-md text-[10px] font-semibold" style={{ background: "#0F2A1F", color: "#4FD18E" }}>{l.action}</span></td><td className="px-4">{l.entity}</td><td className="px-4" style={{ color: "#7A7A72", fontFamily: "monospace" }}>{(l.entity_id || "").slice(0, 14)}</td></tr>)}</tbody></table></Card></div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// 12. SYSTEM
// ═════════════════════════════════════════════════════════════
export function OwnerSystem() {
  const [h, setH] = useState(null);
  useEffect(() => { ownerApi.get("/system/health").then((r) => setH(r.data)); }, []);
  if (!h) return <div className="p-6 text-xs" style={{ color: "#7A7A72" }}>Loading…</div>;
  return (
    <div>
      <PageHeader title="System Health" subtitle="Latency, uptime, providers" />
      <div className="p-6 space-y-5">
        <div className="grid grid-cols-4 gap-3">
          <KpiCard label="Uptime 24h" value={`${h.uptime_pct_24h}%`} tone="good" testid="sys-uptime" />
          <KpiCard label="Error rate" value={`${h.error_rate_pct}%`} tone={h.error_rate_pct > 2 ? "bad" : "good"} />
          <KpiCard label="p50 latency" value={`${h.latency_p50_ms}ms`} />
          <KpiCard label="p95 latency" value={`${h.latency_p95_ms}ms`} tone={h.latency_p95_ms > 500 ? "warn" : ""} />
          <KpiCard label="p99 latency" value={`${h.latency_p99_ms}ms`} />
          <KpiCard label="DB pool" value={`${h.db_pool.used}/${h.db_pool.max}`} />
          <KpiCard label="Redis" value={h.redis_health.status} />
          <KpiCard label="AI providers" value={`${h.ai_providers.filter((p) => p.status === "operational").length}/${h.ai_providers.length}`} tone="good" />
        </div>
        <Card>
          <p className="text-[10px] uppercase tracking-wider mb-3" style={{ color: "#7A7A72" }}>AI providers</p>
          <table className="w-full text-xs"><tbody>{h.ai_providers.map((p, i) => <tr key={i} style={{ borderTop: i ? "1px solid #1F2A33" : "none" }}><td className="py-1.5">{p.name}</td><td className="py-1.5 text-right"><span className="px-2 py-0.5 rounded-md text-[10px] font-semibold" style={{ background: p.status === "operational" ? "#0F2A1F" : "#3a1a1f", color: p.status === "operational" ? "#4FD18E" : "#FF7A8A" }}>{p.status.toUpperCase()}</span></td></tr>)}</tbody></table>
        </Card>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// 13. SETTINGS (Admin users mgmt)
// ═════════════════════════════════════════════════════════════
export function OwnerSettings() {
  const [admins, setAdmins] = useState([]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("SUPPORT_ADMIN");
  const [enable2faMsg, setEnable2faMsg] = useState(null);
  const reload = () => ownerApi.get("/admins").then((r) => setAdmins(r.data.admins)).catch(() => {});
  useEffect(() => { reload(); }, []);
  const create = async () => {
    try { await ownerApi.post("/admins", { email, password, role }); setEmail(""); setPassword(""); reload(); } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };
  const enable2fa = async () => {
    try {
      const { data } = await ownerApi.post("/auth/enable-2fa");
      setEnable2faMsg(`Secret: ${data.secret}\nScan: ${data.otpauth_url}`);
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div>
      <PageHeader title="Settings" subtitle="Admin users, roles, security" />
      <div className="p-6 space-y-5">
        <Card>
          <p className="text-[10px] uppercase tracking-wider mb-3" style={{ color: "#7A7A72" }}>My Account · Security</p>
          <button onClick={enable2fa} className="text-xs px-3 py-1.5 rounded-lg" style={{ background: "#0F2A1F", color: "#4FD18E" }} data-testid="enable-2fa-btn">Enable 2FA (TOTP)</button>
          {enable2faMsg && <pre className="text-[11px] mt-3 p-3 rounded-lg whitespace-pre-wrap" style={{ background: "#0A0F14", color: "#C9A96E", border: "1px solid #1F2A33" }}>{enable2faMsg}</pre>}
        </Card>
        <Card>
          <p className="text-[10px] uppercase tracking-wider mb-3" style={{ color: "#7A7A72" }}>Add new admin</p>
          <div className="grid grid-cols-4 gap-2">
            <input placeholder="email" value={email} onChange={(e) => setEmail(e.target.value)} className="px-3 py-2 rounded-lg text-xs" style={{ background: "#0A0F14", border: "1px solid #1F2A33", color: "#E8E4DC" }} data-testid="new-admin-email" />
            <input type="password" placeholder="password" value={password} onChange={(e) => setPassword(e.target.value)} className="px-3 py-2 rounded-lg text-xs" style={{ background: "#0A0F14", border: "1px solid #1F2A33", color: "#E8E4DC" }} data-testid="new-admin-password" />
            <select value={role} onChange={(e) => setRole(e.target.value)} className="px-3 py-2 rounded-lg text-xs" style={{ background: "#0A0F14", border: "1px solid #1F2A33", color: "#E8E4DC" }} data-testid="new-admin-role">{["SUPER_ADMIN", "FINANCE_ADMIN", "SUPPORT_ADMIN", "ANALYTICS_ADMIN", "MODERATOR"].map((r) => <option key={r}>{r}</option>)}</select>
            <button onClick={create} className="px-3 py-2 rounded-lg text-xs" style={{ background: "#006039", color: "white" }} data-testid="create-admin-btn">Create</button>
          </div>
        </Card>
        <Card className="!p-0">
          <table className="w-full text-xs" data-testid="admins-table"><thead><tr style={{ background: "#0A0F14", color: "#7A7A72" }}>{["Email", "Role", "2FA", "Last login", "Status"].map((h) => <th key={h} className="text-left px-4 py-2.5 font-semibold uppercase tracking-wider text-[10px]">{h}</th>)}</tr></thead><tbody>{admins.map((a) => <tr key={a.id} style={{ borderTop: "1px solid #1F2A33" }}><td className="px-4 py-2.5">{a.email}</td><td><span className="px-2 py-0.5 rounded-md text-[10px] font-semibold" style={{ background: a.role === "OWNER" ? "#2A1F0F" : "#0F2A1F", color: a.role === "OWNER" ? "#C9A96E" : "#4FD18E" }}>{a.role}</span></td><td>{a.two_factor_enabled ? <span style={{ color: "#4FD18E" }}>✓</span> : <span style={{ color: "#7A7A72" }}>—</span>}</td><td style={{ color: "#7A7A72" }}>{(a.last_login_at || "").slice(0, 10) || "—"}</td><td>{a.is_active ? <span style={{ color: "#4FD18E" }}>Active</span> : <span style={{ color: "#FF7A8A" }}>Disabled</span>}</td></tr>)}</tbody></table>
        </Card>
      </div>
    </div>
  );
}
